import React, { useState, useEffect } from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  makeStyles,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Select,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
} from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import { toast } from "react-toastify";

import { 
  Star as StarIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Phone as PhoneIcon,
  Queue as QueueIcon,
  AttachMoney as MoneyIcon,
  Notifications as CampaignIcon,
  Schedule as ScheduleIcon,
  Chat as ChatIcon,
  Http as ApiIcon,
  ViewList as KanbanIcon,
  Android as AiIcon,
  Extension as IntegrationIcon,
  Business as BusinessIcon
} from "@material-ui/icons";

import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(2),
  },
  // ✅ CORREGIDO: FormCard dinámico según tema
  formCard: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.mode === 'dark' ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    marginBottom: theme.spacing(3),
  },
  formCardContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  formTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
    textAlign: "center",
    justifyContent: "center",
  },
  formGrid: {
    marginTop: theme.spacing(1),
  },
  fieldContainer: {
    marginBottom: theme.spacing(1),
  },
  // ✅ CORREGIDO: TextField dinámico según tema
  textField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      borderRadius: theme.spacing(1),
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover": {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : "#fafafa",
      },
      "&.Mui-focused": {
        backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      fontWeight: "500",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  // ✅ CORREGIDO: SelectField dinámico según tema
  selectField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      borderRadius: theme.spacing(1),
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      fontWeight: "500",
    },
    "& .MuiMenuItem-root": {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#f5f5f5',
      },
    },
  },
  buttonContainer: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "center",
    marginTop: theme.spacing(2),
    flexWrap: "wrap",
  },
  actionButton: {
    borderRadius: theme.spacing(1),
    textTransform: "none",
    fontWeight: "600",
    padding: theme.spacing(1, 2),
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    "&:hover": {
      boxShadow: "0 2px 6px rgba(0,0,0,0.16)",
    },
    transition: "all 0.2s ease",
    minWidth: 100,
  },
  primaryButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  secondaryButton: {
    backgroundColor: theme.palette.grey[500],
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.grey[600],
    },
  },
  errorButton: {
    backgroundColor: theme.palette.error.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  // ✅ CORREGIDO: TableCard dinámico según tema
  tableCard: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.mode === 'dark' ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    overflow: "auto",
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "& .MuiTableCell-head": {
      color: "white",
      fontWeight: "bold",
    },
  },
  // ✅ CORREGIDO: TableRow dinámico según tema
  tableRow: {
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.action.hover,
    },
    cursor: "pointer",
  },
  // ✅ CORREGIDO: TableCell dinámico según tema
  tableCell: {
    padding: theme.spacing(1),
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.mode === 'dark' ? '#4B5563' : 'rgba(224, 224, 224, 0.5)'}`,
  },
  editButton: {
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : theme.palette.primary.light,
      color: theme.mode === 'dark' ? theme.palette.primary.light : "white",
    },
  },
  enabledChip: {
    backgroundColor: "#4caf50",
    color: "white",
    fontWeight: "bold",
  },
  disabledChip: {
    backgroundColor: "#f44336",
    color: "white",
    fontWeight: "bold",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
  },
}));

export function PlanFormImproved(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const classes = useStyles();

  const [record, setRecord] = useState({
    name: '',
    users: 0,
    connections: 0,
    queues: 0,
    value: 0,
    useCampaigns: true,
    useSchedules: true,
    useInternalChat: true,
    useExternalApi: true,
    useKanban: true,
    useOpenAi: true,
    useIntegrations: true,
    useInternal: true
  });

  useEffect(() => {
    setRecord(initialValue);
  }, [initialValue]);

  const handleSubmit = async (data) => {
    onSubmit(data);
  };

  return (
    <Card className={classes.formCard}>
      <CardContent className={classes.formCardContent}>
        <Typography variant="h5" className={classes.formTitle}>
          <SettingsIcon />
          {record.id ? "Editar Plan" : "Crear Nuevo Plan"}
        </Typography>
        
        <Formik
          enableReinitialize
          initialValues={record}
          onSubmit={(values, { resetForm }) =>
            setTimeout(() => {
              handleSubmit(values);
              resetForm();
            }, 500)
          }
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid container spacing={1} className={classes.formGrid}>
                {/* Información Básica */}
                <Grid item xs={12}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    <StarIcon />
                    Información Básica del Plan
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("plans.form.name")}
                      name="name"
                      value={values.name || ""}
                      onChange={(e) => setFieldValue('name', e.target.value)}
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <StarIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                  </div>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("plans.form.users")}
                      name="users"
                      value={values.users || ""}
                      onChange={(e) => setFieldValue('users', e.target.value)}
                      variant="outlined"
                      fullWidth
                      type="number"
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <GroupIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                  </div>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("plans.form.connections")}
                      name="connections"
                      value={values.connections || ""}
                      onChange={(e) => setFieldValue('connections', e.target.value)}
                      variant="outlined"
                      fullWidth
                      type="number"
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <PhoneIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                  </div>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("plans.form.queues")}
                      name="queues"
                      value={values.queues || ""}
                      onChange={(e) => setFieldValue('queues', e.target.value)}
                      variant="outlined"
                      fullWidth
                      type="number"
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <QueueIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                  </div>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("plans.form.value")}
                      name="value"
                      value={values.value || ""}
                      onChange={(e) => setFieldValue('value', e.target.value)}
                      variant="outlined"
                      fullWidth
                      type="text"
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <MoneyIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                  </div>
                </Grid>

                {/* Funcionalidades */}
                <Grid item xs={12}>
                  <Divider style={{ margin: "15px 0" }} />
                  <Typography variant="h6" className={classes.sectionTitle}>
                    <SettingsIcon />
                    Funcionalidades del Plan
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.campaigns")}</InputLabel>
                      <Field
                        as={Select}
                        name="useCampaigns"
                        label={i18n.t("plans.form.campaigns")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CampaignIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CampaignIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.schedules")}</InputLabel>
                      <Field
                        as={Select}
                        name="useSchedules"
                        label={i18n.t("plans.form.schedules")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ScheduleIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ScheduleIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.internalChat")}</InputLabel>
                      <Field
                        as={Select}
                        name="useInternalChat"
                        label={i18n.t("plans.form.internalChat")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ChatIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ChatIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.externalApi")}</InputLabel>
                      <Field
                        as={Select}
                        name="useExternalApi"
                        label={i18n.t("plans.form.externalApi")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ApiIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ApiIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.kanban")}</InputLabel>
                      <Field
                        as={Select}
                        name="useKanban"
                        label={i18n.t("plans.form.kanban")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <KanbanIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <KanbanIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.openAi")}</InputLabel>
                      <Field
                        as={Select}
                        name="useOpenAi"
                        label={i18n.t("plans.form.openAi")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AiIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AiIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.integrations")}</InputLabel>
                      <Field
                        as={Select}
                        name="useIntegrations"
                        label={i18n.t("plans.form.integrations")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <IntegrationIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <IntegrationIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.internalUse")}</InputLabel>
                      <Field
                        as={Select}
                        name="useInternal"
                        label={i18n.t("plans.form.internalUse")}
                      >
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <BusinessIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.yes")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <BusinessIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.no")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>
              </Grid>

              {/* Botones de Acción */}
              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  className={`${classes.actionButton} ${classes.secondaryButton}`}
                  onClick={onCancel}
                  startIcon={<ClearIcon />}
                  disabled={loading}
                >
                  {i18n.t("plans.form.clear")}
                </Button>
                
                {record.id !== undefined && (
                  <Button
                    variant="contained"
                    className={`${classes.actionButton} ${classes.errorButton}`}
                    onClick={() => onDelete(record)}
                    startIcon={<DeleteIcon />}
                    disabled={loading}
                  >
                    {i18n.t("plans.form.delete")}
                  </Button>
                )}
                
                <Button
                  type="submit"
                  variant="contained"
                  className={`${classes.actionButton} ${classes.primaryButton}`}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                  {loading ? "Guardando..." : i18n.t("plans.form.save")}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
}

export function PlansManagerGridImproved(props) {
  const { records, onSelect } = props;
  const classes = useStyles();
  
  const renderStatus = (value) => {
    return value ? (
      <Chip label="Sí" size="small" className={classes.enabledChip} />
    ) : (
      <Chip label="No" size="small" className={classes.disabledChip} />
    );
  };

  const renderInternal = (value) => {
    return !value ? (
      <Chip label="Sí" size="small" className={classes.enabledChip} />
    ) : (
      <Chip label="No" size="small" className={classes.disabledChip} />
    );
  };

  if (records.length === 0) {
    return (
      <Card className={classes.tableCard}>
        <CardContent className={classes.emptyState}>
          <Typography variant="h6" color="textSecondary">
            No hay planes registrados
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Crea tu primer plan para comenzar
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.tableCard}>
      <Table>
        <TableHead className={classes.tableHeader}>
          <TableRow>
            <TableCell align="center" style={{ width: "60px" }}>#</TableCell>
            <TableCell align="left">
              <StarIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.name")}
            </TableCell>
            <TableCell align="center">
              <GroupIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.users")}
            </TableCell>
            <TableCell align="center">
              <PhoneIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.connections")}
            </TableCell>
            <TableCell align="center">
              <QueueIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.queues")}
            </TableCell>
            <TableCell align="center">
              <MoneyIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.value")}
            </TableCell>
            <TableCell align="center">
              <CampaignIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.campaigns")}
            </TableCell>
            <TableCell align="center">
              <ScheduleIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.schedules")}
            </TableCell>
            <TableCell align="center">
              <ChatIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.internalChat")}
            </TableCell>
            <TableCell align="center">
              <ApiIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.externalApi")}
            </TableCell>
            <TableCell align="center">
              <KanbanIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.kanban")}
            </TableCell>
            <TableCell align="center">
              <AiIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.openAi")}
            </TableCell>
            <TableCell align="center">
              <IntegrationIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.integrations")}
            </TableCell>
            <TableCell align="center">
              <BusinessIcon style={{ marginRight: 8 }} />
              {i18n.t("plans.form.internalPlan")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row, key) => (
            <TableRow 
              className={classes.tableRow} 
              key={key}
              onClick={() => onSelect(row)}
            >
              <TableCell align="center" className={classes.tableCell}>
                <Tooltip title="Editar plan">
                  <IconButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(row);
                    }} 
                    className={classes.editButton}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell align="left" className={classes.tableCell}>
                <Typography variant="body2" style={{ fontWeight: "bold" }}>
                  {row.name || '-'}
                </Typography>
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                {row.users || '-'}
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                {row.connections || '-'}
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                {row.queues || '-'}
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                <Typography variant="body2" style={{ fontWeight: "bold", color: "#2e7d32" }}>
                  ${row.value ? row.value.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00'}
                </Typography>
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                {renderStatus(row.useCampaigns)}
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                {renderStatus(row.useSchedules)}
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                {renderStatus(row.useInternalChat)}
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                {renderStatus(row.useExternalApi)}
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                {renderStatus(row.useKanban)}
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                {renderStatus(row.useOpenAi)}
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                {renderStatus(row.useIntegrations)}
              </TableCell>
              <TableCell align="center" className={classes.tableCell}>
                {renderInternal(row.useInternal)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

export default function PlansManagerImproved() {
  const classes = useStyles();
  const { list, save, update, remove } = usePlans();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({
    name: '',
    users: 0,
    connections: 0,
    queues: 0,
    value: 0,
    useCampaigns: true,
    useSchedules: true,
    useInternalChat: true,
    useExternalApi: true,
    useKanban: true,
    useOpenAi: true,
    useIntegrations: true,
    useInternal: true
  });

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const planList = await list();
      setRecords(planList);
    } catch (e) {
      toast.error(i18n.t("plans.messages.loadError"));
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadPlans();
      handleCancel();
      toast.success(i18n.t("plans.messages.saveSuccess"));
    } catch (e) {
      toast.error(i18n.t("plans.messages.saveError"));
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCancel();
      toast.success(i18n.t("plans.messages.deleteSuccess"));
    } catch (e) {
      toast.error(i18n.t("plans.messages.deleteError"));
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setRecord({
      id: undefined,
      name: '',
      users: 0,
      connections: 0,
      queues: 0,
      value: 0,
      useCampaigns: true,
      useSchedules: true,
      useInternalChat: true,
      useExternalApi: true,
      useKanban: true,
      useOpenAi: true,
      useIntegrations: true,
      useInternal: true
    });
  };

  const handleSelect = (data) => {
    let useCampaigns = data.useCampaigns === false ? false : true;
    let useSchedules = data.useSchedules === false ? false : true;
    let useInternalChat = data.useInternalChat === false ? false : true;
    let useExternalApi = data.useExternalApi === false ? false : true;
    let useKanban = data.useKanban === false ? false : true;
    let useOpenAi = data.useOpenAi === false ? false : true;
    let useIntegrations = data.useIntegrations === false ? false : true;
    let useInternal = data.useInternal === true ? true : false;

    setRecord({
      id: data.id,
      name: data.name || '',
      users: data.users || 0,
      connections: data.connections || 0,
      queues: data.queues || 0,
      value: data.value?.toLocaleString('pt-br', { minimumFractionDigits: 0 }) || 0,
      useCampaigns,
      useSchedules,
      useInternalChat,
      useExternalApi,
      useKanban,
      useOpenAi,
      useIntegrations,
      useInternal
    });
  };

  return (
    <div className={classes.root}>
      <PlanFormImproved
        initialValue={record}
        onDelete={handleOpenDeleteDialog}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={loading}
      />
      
      <PlansManagerGridImproved
        records={records}
        onSelect={handleSelect}
      />
      
      <ConfirmationModal
        title={i18n.t("plans.messages.deleteTitle")}
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        {i18n.t("plans.messages.deleteMessage")}
      </ConfirmationModal>
    </div>
  );
}
